import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { collection, doc, getDocs, query, updateDoc, where } from "firebase/firestore";
import { ref, update } from "firebase/database";
import { useEffect, useMemo, useState } from "react";
import { Check, ChevronRight, SquarePlus, X } from "lucide-react";
import { db, realtimeDb } from "../firebase/config";
import { appendSeasonToAthletes, queryPreviousAthletes } from "../roster/athleteQueries";
import { AthleteSelectionDialog } from "../roster/AthleteSelectionDialog";
import { formatSeasonName, formatTeamName } from "../teams/TeamProvider";
const seasonTypes = ["cross country", "indoor", "outdoor"];
function nextSeasonName(currentSeason) {
    const normalized = currentSeason?.name.trim().toLowerCase();
    if (normalized === "cross country")
        return "indoor";
    if (normalized === "indoor")
        return "outdoor";
    return "cross country";
}
function suggestedYear(currentSeason, nextName) {
    if (!currentSeason?.year)
        return new Date().getFullYear();
    const current = currentSeason.name.trim().toLowerCase();
    if (current === "outdoor" && nextName === "cross country")
        return currentSeason.year;
    if (current === "cross country" && nextName === "indoor")
        return currentSeason.year;
    if (current === "indoor" && nextName === "outdoor")
        return currentSeason.year + 1;
    return currentSeason.year;
}
function managerName(manager) {
    return manager.name || [manager.firstName, manager.lastName].filter(Boolean).join(" ") || manager.email || "Manager";
}
function capitalizeWords(value) {
    return value.trim().replace(/\s+/g, " ").replace(/\b\S/g, (letter) => letter.toUpperCase());
}
function seasonTeamName(team) {
    const adultDivision = team.level === "college" || team.level === "club";
    const division = team.gender === "female"
        ? adultDivision ? "Women's" : "Girls'"
        : adultDivision ? "Men's" : "Boys'";
    return `${team.name} ${division}`;
}
function fullSeasonTeamName(team, season) {
    const teamName = seasonTeamName(team);
    const normalized = season.name.trim().toLowerCase();
    const description = season.description?.trim();
    const middle = description ? ` ${description}` : "";
    if (normalized === "cross country")
        return `${teamName}${middle} Cross Country ${season.year}`;
    if (normalized === "indoor")
        return `${teamName}${middle} Indoor Track ${season.year} - ${season.year + 1}`;
    return `${teamName}${middle} Outdoor Track ${season.year}`;
}
async function loadManagers(teamId) {
    const snapshot = await getDocs(query(collection(db, "users"), where(`teams.${teamId}`, "==", "manager")));
    return snapshot.docs.map((managerDocument) => ({ id: managerDocument.id, ...managerDocument.data() }))
        .sort((left, right) => managerName(left).localeCompare(managerName(right)));
}
export function AddSeasonDialog({ team, currentSeason, onClose, onCreated }) {
    const initialName = nextSeasonName(currentSeason);
    const [seasonName, setSeasonName] = useState(initialName);
    const [year, setYear] = useState(String(suggestedYear(currentSeason, initialName)));
    const [description, setDescription] = useState("");
    const [askManagers, setAskManagers] = useState("idle");
    const [managers, setManagers] = useState([]);
    const [managerLoading, setManagerLoading] = useState(false);
    const [managerError, setManagerError] = useState("");
    const [selectedManagerIds, setSelectedManagerIds] = useState(new Set());
    const [askAthletes, setAskAthletes] = useState("idle");
    const [athleteSelectorOpen, setAthleteSelectorOpen] = useState(false);
    const [athletes, setAthletes] = useState([]);
    const [selectedAthletes, setSelectedAthletes] = useState([]);
    const [athleteLoading, setAthleteLoading] = useState(false);
    const [athleteError, setAthleteError] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    useEffect(() => {
        const nextName = nextSeasonName(currentSeason);
        setSeasonName(nextName);
        setYear(String(suggestedYear(currentSeason, nextName)));
    }, [currentSeason?.id]);
    useEffect(() => {
        function close(event) {
            if (event.key === "Escape" && !saving)
                onClose();
        }
        document.addEventListener("keydown", close);
        return () => document.removeEventListener("keydown", close);
    }, [onClose, saving]);
    async function chooseManagers(answer) {
        setAskManagers(answer);
        setManagerError("");
        if (answer === "no" || managers.length > 0)
            return;
        setManagerLoading(true);
        try {
            setManagers(await loadManagers(team.id));
        }
        catch (caughtError) {
            setManagerError(caughtError instanceof Error ? caughtError.message : "Managers could not be loaded.");
        }
        finally {
            setManagerLoading(false);
        }
    }
    async function openPreviousAthleteSelector() {
        setAthleteSelectorOpen(true);
        setAthleteLoading(true);
        setAthleteError("");
        try {
            setAthletes(await queryPreviousAthletes(team, draftSeason));
        }
        catch (caughtError) {
            setAthleteError(caughtError instanceof Error ? caughtError.message : "Previous athletes could not be loaded.");
        }
        finally {
            setAthleteLoading(false);
        }
    }
    async function chooseAthletes(answer) {
        setAskAthletes(answer);
        setAthleteError("");
        if (answer === "no")
            return;
        await openPreviousAthleteSelector();
    }
    function toggleManager(managerId) {
        setSelectedManagerIds((current) => {
            const next = new Set(current);
            if (next.has(managerId))
                next.delete(managerId);
            else
                next.add(managerId);
            return next;
        });
    }
    const yearNumber = Number(year);
    const draftSeason = useMemo(() => ({ id: "preview", name: seasonName, year: Number.isInteger(yearNumber) ? yearNumber : new Date().getFullYear(), description: capitalizeWords(description) || null }), [description, seasonName, yearNumber]);
    const previewName = year.length === 4 && Number.isInteger(yearNumber) ? fullSeasonTeamName(team, draftSeason) : "Enter a year.";
    const hasPreviousSeasons = Object.keys(team.seasons ?? {}).length > 0;
    const hasManagers = (team.managers ?? []).some((managerId) => managerId !== team.owner.id);
    const schoolTeam = team.level === "school" || team.level === "college";
    useEffect(() => {
        if (!schoolTeam)
            return;
        setSelectedAthletes([]);
        setAthletes([]);
        setAthleteError("");
        setAthleteSelectorOpen(false);
    }, [schoolTeam, year]);
    async function saveSeason(event) {
        event.preventDefault();
        const normalizedDescription = capitalizeWords(description);
        if (!/^\d{4}$/.test(year) || !Number.isInteger(yearNumber)) {
            setError("Enter a valid year.");
            return;
        }
        if (yearNumber < 2017) {
            setError("Year must be 2017 or later.");
            return;
        }
        if (normalizedDescription.length > 20) {
            setError("Description must be 20 characters or less.");
            return;
        }
        setSaving(true);
        setError("");
        try {
            const seasonRef = doc(collection(db, "teams", team.id, "seasonIds"));
            const season = { id: seasonRef.id, name: seasonName, year: yearNumber, ...(normalizedDescription ? { description: normalizedDescription } : {}) };
            const firestoreUpdates = { [`seasons.${season.id}`]: season };
            await updateDoc(doc(db, "teams", team.id), firestoreUpdates);
            const accessUpdates = { [`seasons/${season.id}/locked`]: null };
            selectedManagerIds.forEach((managerId) => { accessUpdates[`seasons/${season.id}/managers/${managerId}`] = true; });
            await update(ref(realtimeDb, `access/${team.id}`), accessUpdates);
            if (selectedAthletes.length > 0)
                await appendSeasonToAthletes(team.id, selectedAthletes.map((athlete) => athlete.id), season.id);
            onCreated(season);
        }
        catch (caughtError) {
            setError(caughtError instanceof Error ? caughtError.message : "The season could not be created.");
        }
        finally {
            setSaving(false);
        }
    }
    const canSubmit = /^\d{4}$/.test(year) && (!hasManagers || askManagers !== "idle") && (!hasPreviousSeasons || askAthletes !== "idle");
    return _jsx("div", { className: "season-dialog-backdrop", role: "presentation", children: _jsxs("section", { className: "season-dialog", role: "dialog", "aria-modal": "true", "aria-labelledby": "new-season-title", onMouseDown: (event) => event.stopPropagation(), children: [_jsxs("header", { children: [_jsx("div", { className: "season-dialog__icon", children: _jsx(SquarePlus, { size: 22 }) }), _jsxs("div", { children: [_jsx("h2", { id: "new-season-title", children: "Add Season" }), _jsx("p", { children: formatTeamName(team) })] }), _jsx("button", { type: "button", disabled: saving, onClick: onClose, "aria-label": "Close", children: _jsx(X, { size: 20 }) })] }), _jsxs("form", { onSubmit: saveSeason, children: [_jsxs("div", { className: "season-dialog__grid", children: [_jsxs("label", { children: ["Season", _jsxs("select", { value: seasonName, onChange: (event) => setSeasonName(event.target.value), children: [_jsx("option", { value: "cross country", children: "Cross Country" }), _jsx("option", { value: "indoor", children: "Indoor" }), _jsx("option", { value: "outdoor", children: "Outdoor" })] })] }), _jsxs("label", { children: ["Year", _jsx("input", { value: year, onChange: (event) => setYear(event.target.value.replace(/\D/g, "").slice(0, 4)), inputMode: "numeric", pattern: "[0-9]{4}", required: true, placeholder: "YYYY" })] })] }), _jsxs("label", { children: ["Description ", _jsxs("small", { children: [description.length, "/20"] }), _jsx("input", { value: description, onChange: (event) => setDescription(event.target.value.slice(0, 20)), maxLength: 20, placeholder: "Varsity" })] }), _jsxs("div", { className: "season-name-preview", children: [_jsx("span", { children: "Season Preview" }), _jsx("strong", { children: previewName })] }), hasManagers && _jsxs("section", { className: "season-optional-section", children: [_jsxs("header", { children: [_jsxs("div", { children: [_jsx("strong", { children: "Manager write access" }), _jsx("span", { children: "Would you like to grant access to current managers now?" })] }), _jsxs("div", { children: [_jsx("button", { type: "button", className: askManagers === "yes" ? "active" : "", onClick: () => void chooseManagers("yes"), children: "Yes" }), _jsx("button", { type: "button", className: askManagers === "no" ? "active" : "", onClick: () => void chooseManagers("no"), children: "No" })] })] }), askManagers === "yes" && _jsx("div", { className: "season-manager-picker", children: _jsxs("div", { className: "season-manager-list", children: [managerLoading && _jsx("div", { className: "season-picker-state", children: "Loading managers..." }), !managerLoading && managerError && _jsx("div", { className: "season-picker-state season-picker-state--error", children: managerError }), !managerLoading && !managerError && managers.map((manager) => {
                                                const checked = selectedManagerIds.has(manager.id);
                                                return _jsxs("button", { className: checked ? "selected" : "", type: "button", onClick: () => toggleManager(manager.id), children: [_jsxs("span", { children: [_jsx("strong", { children: managerName(manager) }), manager.email && _jsx("small", { children: manager.email })] }), _jsx("span", { className: "athlete-selector-check", children: checked && _jsx(Check, { size: 14 }) })] }, manager.id);
                                            }), !managerLoading && !managerError && managers.length === 0 && _jsx("div", { className: "season-picker-state", children: "No active managers found." })] }) })] }), hasPreviousSeasons && _jsxs("section", { className: "season-optional-section", children: [_jsxs("header", { children: [_jsxs("div", { children: [_jsx("strong", { children: "Previous athletes" }), _jsxs("span", { children: ["Would you like to import eligible athletes into ", formatSeasonName(draftSeason), "?"] })] }), _jsxs("div", { children: [_jsx("button", { type: "button", className: askAthletes === "yes" ? "active" : "", onClick: () => void chooseAthletes("yes"), children: "Yes" }), _jsx("button", { type: "button", className: askAthletes === "no" ? "active" : "", onClick: () => void chooseAthletes("no"), children: "No" })] })] }), askAthletes === "yes" && _jsxs("button", { className: "season-selected-athletes", type: "button", onClick: () => void openPreviousAthleteSelector(), children: [_jsxs("span", { children: [_jsx("strong", { children: selectedAthletes.length }), " athletes selected"] }), _jsx(ChevronRight, { size: 17 })] })] }), error && _jsx("div", { className: "athlete-form-error", children: error }), _jsxs("footer", { children: [_jsx("button", { type: "button", disabled: saving, onClick: onClose, children: "Cancel" }), _jsx("button", { type: "submit", disabled: saving || !canSubmit, children: saving ? "Creating..." : "Create season" })] })] }), athleteSelectorOpen && _jsx(AthleteSelectionDialog, { title: "Import Previous Athletes", description: `Select athletes to add to ${formatSeasonName(draftSeason)}.`, athletes: athletes, loading: athleteLoading, error: athleteError, minSelections: 0, confirmLabel: "Add selected athletes", submitting: saving, onClose: () => setAthleteSelectorOpen(false), onConfirm: (selected) => { setSelectedAthletes(selected); setAthleteSelectorOpen(false); } })] }) });
}
